import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import {
  BRANCHES,
  EMAIL,
  PHONE_DISPLAY,
  telLink,
  waLink,
} from "../../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    branch: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.branch || !form.message) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll reach out shortly.");
      const text = `Hi Justudy! I'm ${form.name}. Branch: ${form.branch}. ${form.message} (Phone: ${form.phone})`;
      window.open(waLink(text), "_blank");
      setForm({ name: "", phone: "", branch: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0F0F0F] border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm focus-visible:ring-[#FACC15]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0B0B0B] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <div className="text-xs tracking-[0.25em] uppercase text-[#FACC15] font-semibold mb-4">
            Contact
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white">
            Reserve your seat.
            <br />
            <span className="text-white/40">Start focusing today.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-[#1A1A1A] border border-white/5 rounded-sm p-6 md:p-9"
          >
            <h3 className="font-display text-2xl font-semibold text-white mb-6">
              Send a message
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                data-testid="contact-name"
                placeholder="Your Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <Input
                data-testid="contact-phone"
                placeholder="Phone Number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="mt-4">
              <Select
                value={form.branch}
                onValueChange={(v) => setForm({ ...form, branch: v })}
              >
                <SelectTrigger
                  data-testid="contact-branch"
                  className={`${inputCls} data-[placeholder]:text-white/30`}
                >
                  <SelectValue placeholder="Branch Preference" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                  {Object.values(BRANCHES).map((b) => (
                    <SelectItem
                      key={b.id}
                      value={b.name}
                      data-testid={`contact-branch-${b.id}`}
                      className="focus:bg-[#FACC15]/10 focus:text-[#FACC15]"
                    >
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              data-testid="contact-message"
              placeholder="Your Message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              className="mt-4 bg-[#0F0F0F] border-white/10 text-white placeholder:text-white/30 rounded-sm focus-visible:ring-[#FACC15]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-50 text-[#0F0F0F] font-semibold px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5"
            >
              <Send className="w-4 h-4" />
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>

          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-sm p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[#FACC15] mb-3 font-semibold">
                Quick Contact
              </div>
              <h4 className="font-display text-xl font-semibold text-white mb-4">
                Justudy Library & Co-working
              </h4>
              <div className="space-y-3 text-sm">
                <a
                  href={telLink}
                  data-testid="contact-info-phone"
                  className="flex items-center gap-3 text-white/80 hover:text-[#FACC15] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#FACC15]" /> {PHONE_DISPLAY}
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-info-whatsapp"
                  className="flex items-center gap-3 text-white/80 hover:text-[#FACC15] transition-colors"
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Us
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-info-email"
                  className="flex items-center gap-3 text-white/80 hover:text-[#FACC15] transition-colors break-all"
                >
                  <Mail className="w-4 h-4 text-[#FACC15]" /> {EMAIL}
                </a>
              </div>
            </div>

            {Object.values(BRANCHES).map((b) => (
              <div
                key={b.id}
                className="bg-[#1A1A1A] border border-white/5 rounded-sm p-6"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-[#FACC15] mb-2 font-semibold">
                  {b.name}
                </div>
                <div className="flex items-start gap-2 text-sm text-white/75 leading-relaxed">
                  <MapPin className="w-4 h-4 text-[#FACC15] mt-0.5 flex-shrink-0" />
                  <span>{b.address}</span>
                </div>
                <a
                  href={b.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-block text-xs uppercase tracking-[0.15em] text-[#FACC15] hover:underline font-semibold"
                >
                  Open in Maps →
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {Object.values(BRANCHES).map((b) => (
            <div
              key={b.id}
              className="bg-[#1A1A1A] border border-white/5 rounded-sm overflow-hidden"
            >
              <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                <div className="font-display text-sm font-semibold text-white">
                  {b.name}
                </div>
                <div className="text-xs text-[#FACC15] font-semibold">{b.timing}</div>
              </div>
              <iframe
                title={`Map ${b.name}`}
                src={b.mapsEmbed}
                className="w-full h-72"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
